/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Impero Org API
 * The Impero Backend's API for organizations, i.e. orgname.impero.com.
 */
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
    UseSuspenseQueryOptions,
    UseSuspenseQueryResult,
} from "@tanstack/react-query";

import axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type {
    BlobId,
    ControlResultSuperInitialStepExternalId,
    ControlResultSuperSubsequentStepExternalId,
    SavedBlob,
    SavedBlobListDto,
} from "../model";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

const withQueryKey = <T extends object, K>(
    query: T,
    queryKey: K
): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === "queryKey") continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const postBlobsControlResultExternalId = (
    externalId: ControlResultSuperInitialStepExternalId,
    postBlobsControlResultExternalIdBody: Blob[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    const formData = new FormData();
    postBlobsControlResultExternalIdBody.forEach((value) =>
        formData.append("data", value)
    );

    return axios.post(
        `/api/blobs/control-result/${externalId}`,
        formData,
        options
    );
};

export const getPostBlobsControlResultExternalIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsControlResultExternalId>>,
        TError,
        { externalId: ControlResultSuperInitialStepExternalId; data: Blob[] },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsControlResultExternalId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; data: Blob[] },
    TContext
> => {
    const mutationKey = ["postBlobsControlResultExternalId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsControlResultExternalId>>,
        { externalId: ControlResultSuperInitialStepExternalId; data: Blob[] }
    > = (props) => {
        const { externalId, data } = props ?? {};

        return postBlobsControlResultExternalId(externalId, data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsControlResultExternalIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsControlResultExternalId>>
>;
export type PostBlobsControlResultExternalIdMutationBody = Blob[];
export type PostBlobsControlResultExternalIdMutationError = AxiosError<unknown>;

export const usePostBlobsControlResultExternalId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsControlResultExternalId>>,
            TError,
            {
                externalId: ControlResultSuperInitialStepExternalId;
                data: Blob[];
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsControlResultExternalId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; data: Blob[] },
    TContext
> => {
    return useMutation(
        getPostBlobsControlResultExternalIdMutationOptions(options),
        queryClient
    );
};
export const getBlobsControlResultExternalIdBlobId = (
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
    return axios.get(`/api/blobs/control-result/${externalId}/${blobId}`, {
        responseType: "blob",
        paramsSerializer: { indexes: null },
        ...options,
    });
};

export const getGetBlobsControlResultExternalIdBlobIdQueryKey = (
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId
) => {
    return [`/api/blobs/control-result/${externalId}/${blobId}`] as const;
};

export const getGetBlobsControlResultExternalIdBlobIdQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getGetBlobsControlResultExternalIdBlobIdQueryKey(externalId, blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>
    > = ({ signal }) =>
        getBlobsControlResultExternalIdBlobId(externalId, blobId, {
            signal,
            ...axiosOptions,
        });

    return {
        queryKey,
        queryFn,
        enabled:
            externalId !== null &&
            externalId !== undefined &&
            blobId !== null &&
            blobId !== undefined,
        ...queryOptions,
    } as UseQueryOptions<
        Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsControlResultExternalIdBlobIdQueryResult = NonNullable<
    Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>
>;
export type GetBlobsControlResultExternalIdBlobIdQueryError =
    AxiosError<unknown>;

export function useGetBlobsControlResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<
                        ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                    >,
                    TError,
                    Awaited<
                        ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                    >
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsControlResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<
                        ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                    >,
                    TError,
                    Awaited<
                        ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                    >
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsControlResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsControlResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetBlobsControlResultExternalIdBlobIdQueryOptions(
        externalId,
        blobId,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetBlobsControlResultExternalIdBlobIdSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getGetBlobsControlResultExternalIdBlobIdQueryKey(externalId, blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>
    > = ({ signal }) =>
        getBlobsControlResultExternalIdBlobId(externalId, blobId, {
            signal,
            ...axiosOptions,
        });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsControlResultExternalIdBlobIdSuspenseQueryResult =
    NonNullable<
        Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>
    >;
export type GetBlobsControlResultExternalIdBlobIdSuspenseQueryError =
    AxiosError<unknown>;

export function useGetBlobsControlResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsControlResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsControlResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsControlResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsControlResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsControlResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions =
        getGetBlobsControlResultExternalIdBlobIdSuspenseQueryOptions(
            externalId,
            blobId,
            options
        );

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const deleteBlobsControlResultExternalIdBlobId = (
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(
        `/api/blobs/control-result/${externalId}/${blobId}`,
        options
    );
};

export const getDeleteBlobsControlResultExternalIdBlobIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>>,
        TError,
        { externalId: ControlResultSuperInitialStepExternalId; blobId: BlobId },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; blobId: BlobId },
    TContext
> => {
    const mutationKey = ["deleteBlobsControlResultExternalIdBlobId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>>,
        { externalId: ControlResultSuperInitialStepExternalId; blobId: BlobId }
    > = (props) => {
        const { externalId, blobId } = props ?? {};

        return deleteBlobsControlResultExternalIdBlobId(
            externalId,
            blobId,
            axiosOptions
        );
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteBlobsControlResultExternalIdBlobIdMutationResult =
    NonNullable<
        Awaited<ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>>
    >;

export type DeleteBlobsControlResultExternalIdBlobIdMutationError =
    AxiosError<unknown>;

export const useDeleteBlobsControlResultExternalIdBlobId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<
                ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>
            >,
            TError,
            {
                externalId: ControlResultSuperInitialStepExternalId;
                blobId: BlobId;
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof deleteBlobsControlResultExternalIdBlobId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; blobId: BlobId },
    TContext
> => {
    return useMutation(
        getDeleteBlobsControlResultExternalIdBlobIdMutationOptions(options),
        queryClient
    );
};
export const postBlobsInfo = (
    blobId: BlobId[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    return axios.post(`/api/blobs/info`, blobId, options);
};

export const getPostBlobsInfoMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsInfo>>,
        TError,
        { data: BlobId[] },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsInfo>>,
    TError,
    { data: BlobId[] },
    TContext
> => {
    const mutationKey = ["postBlobsInfo"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsInfo>>,
        { data: BlobId[] }
    > = (props) => {
        const { data } = props ?? {};

        return postBlobsInfo(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsInfoMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsInfo>>
>;
export type PostBlobsInfoMutationBody = BlobId[];
export type PostBlobsInfoMutationError = AxiosError<unknown>;

export const usePostBlobsInfo = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsInfo>>,
            TError,
            { data: BlobId[] },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsInfo>>,
    TError,
    { data: BlobId[] },
    TContext
> => {
    return useMutation(getPostBlobsInfoMutationOptions(options), queryClient);
};
export const postBlobsInfoControlResultExternalId = (
    externalId: ControlResultSuperInitialStepExternalId,
    blobId: BlobId[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    return axios.post(
        `/api/blobs/info/control-result/${externalId}`,
        blobId,
        options
    );
};

export const getPostBlobsInfoControlResultExternalIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>,
        TError,
        { externalId: ControlResultSuperInitialStepExternalId; data: BlobId[] },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; data: BlobId[] },
    TContext
> => {
    const mutationKey = ["postBlobsInfoControlResultExternalId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>,
        { externalId: ControlResultSuperInitialStepExternalId; data: BlobId[] }
    > = (props) => {
        const { externalId, data } = props ?? {};

        return postBlobsInfoControlResultExternalId(
            externalId,
            data,
            axiosOptions
        );
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsInfoControlResultExternalIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>
>;
export type PostBlobsInfoControlResultExternalIdMutationBody = BlobId[];
export type PostBlobsInfoControlResultExternalIdMutationError =
    AxiosError<unknown>;

export const usePostBlobsInfoControlResultExternalId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>,
            TError,
            {
                externalId: ControlResultSuperInitialStepExternalId;
                data: BlobId[];
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsInfoControlResultExternalId>>,
    TError,
    { externalId: ControlResultSuperInitialStepExternalId; data: BlobId[] },
    TContext
> => {
    return useMutation(
        getPostBlobsInfoControlResultExternalIdMutationOptions(options),
        queryClient
    );
};
export const postBlobsInfoReviewResultExternalId = (
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    return axios.post(
        `/api/blobs/info/review-result/${externalId}`,
        blobId,
        options
    );
};

export const getPostBlobsInfoReviewResultExternalIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>,
        TError,
        {
            externalId: ControlResultSuperSubsequentStepExternalId;
            data: BlobId[];
        },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; data: BlobId[] },
    TContext
> => {
    const mutationKey = ["postBlobsInfoReviewResultExternalId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>,
        {
            externalId: ControlResultSuperSubsequentStepExternalId;
            data: BlobId[];
        }
    > = (props) => {
        const { externalId, data } = props ?? {};

        return postBlobsInfoReviewResultExternalId(
            externalId,
            data,
            axiosOptions
        );
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsInfoReviewResultExternalIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>
>;
export type PostBlobsInfoReviewResultExternalIdMutationBody = BlobId[];
export type PostBlobsInfoReviewResultExternalIdMutationError =
    AxiosError<unknown>;

export const usePostBlobsInfoReviewResultExternalId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>,
            TError,
            {
                externalId: ControlResultSuperSubsequentStepExternalId;
                data: BlobId[];
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsInfoReviewResultExternalId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; data: BlobId[] },
    TContext
> => {
    return useMutation(
        getPostBlobsInfoReviewResultExternalIdMutationOptions(options),
        queryClient
    );
};
export const postBlobsReviewResultExternalId = (
    externalId: ControlResultSuperSubsequentStepExternalId,
    postBlobsReviewResultExternalIdBody: Blob[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    const formData = new FormData();
    postBlobsReviewResultExternalIdBody.forEach((value) =>
        formData.append("data", value)
    );

    return axios.post(
        `/api/blobs/review-result/${externalId}`,
        formData,
        options
    );
};

export const getPostBlobsReviewResultExternalIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>,
        TError,
        {
            externalId: ControlResultSuperSubsequentStepExternalId;
            data: Blob[];
        },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; data: Blob[] },
    TContext
> => {
    const mutationKey = ["postBlobsReviewResultExternalId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>,
        { externalId: ControlResultSuperSubsequentStepExternalId; data: Blob[] }
    > = (props) => {
        const { externalId, data } = props ?? {};

        return postBlobsReviewResultExternalId(externalId, data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsReviewResultExternalIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>
>;
export type PostBlobsReviewResultExternalIdMutationBody = Blob[];
export type PostBlobsReviewResultExternalIdMutationError = AxiosError<unknown>;

export const usePostBlobsReviewResultExternalId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>,
            TError,
            {
                externalId: ControlResultSuperSubsequentStepExternalId;
                data: Blob[];
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsReviewResultExternalId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; data: Blob[] },
    TContext
> => {
    return useMutation(
        getPostBlobsReviewResultExternalIdMutationOptions(options),
        queryClient
    );
};
export const getBlobsReviewResultExternalIdBlobId = (
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
    return axios.get(`/api/blobs/review-result/${externalId}/${blobId}`, {
        responseType: "blob",
        paramsSerializer: { indexes: null },
        ...options,
    });
};

export const getGetBlobsReviewResultExternalIdBlobIdQueryKey = (
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId
) => {
    return [`/api/blobs/review-result/${externalId}/${blobId}`] as const;
};

export const getGetBlobsReviewResultExternalIdBlobIdQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getGetBlobsReviewResultExternalIdBlobIdQueryKey(externalId, blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>
    > = ({ signal }) =>
        getBlobsReviewResultExternalIdBlobId(externalId, blobId, {
            signal,
            ...axiosOptions,
        });

    return {
        queryKey,
        queryFn,
        enabled:
            externalId !== null &&
            externalId !== undefined &&
            blobId !== null &&
            blobId !== undefined,
        ...queryOptions,
    } as UseQueryOptions<
        Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsReviewResultExternalIdBlobIdQueryResult = NonNullable<
    Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>
>;
export type GetBlobsReviewResultExternalIdBlobIdQueryError =
    AxiosError<unknown>;

export function useGetBlobsReviewResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<
                        ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                    >,
                    TError,
                    Awaited<
                        ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                    >
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsReviewResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<
                        ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                    >,
                    TError,
                    Awaited<
                        ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                    >
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsReviewResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsReviewResultExternalIdBlobId<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetBlobsReviewResultExternalIdBlobIdQueryOptions(
        externalId,
        blobId,
        options
    );

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetBlobsReviewResultExternalIdBlobIdSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ??
        getGetBlobsReviewResultExternalIdBlobIdQueryKey(externalId, blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>
    > = ({ signal }) =>
        getBlobsReviewResultExternalIdBlobId(externalId, blobId, {
            signal,
            ...axiosOptions,
        });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsReviewResultExternalIdBlobIdSuspenseQueryResult =
    NonNullable<
        Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>
    >;
export type GetBlobsReviewResultExternalIdBlobIdSuspenseQueryError =
    AxiosError<unknown>;

export function useGetBlobsReviewResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsReviewResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsReviewResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsReviewResultExternalIdBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsReviewResultExternalIdBlobId>>,
    TError = AxiosError<unknown>,
>(
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<
                    ReturnType<typeof getBlobsReviewResultExternalIdBlobId>
                >,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions =
        getGetBlobsReviewResultExternalIdBlobIdSuspenseQueryOptions(
            externalId,
            blobId,
            options
        );

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const deleteBlobsReviewResultExternalIdBlobId = (
    externalId: ControlResultSuperSubsequentStepExternalId,
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(
        `/api/blobs/review-result/${externalId}/${blobId}`,
        options
    );
};

export const getDeleteBlobsReviewResultExternalIdBlobIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>,
        TError,
        {
            externalId: ControlResultSuperSubsequentStepExternalId;
            blobId: BlobId;
        },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; blobId: BlobId },
    TContext
> => {
    const mutationKey = ["deleteBlobsReviewResultExternalIdBlobId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>,
        {
            externalId: ControlResultSuperSubsequentStepExternalId;
            blobId: BlobId;
        }
    > = (props) => {
        const { externalId, blobId } = props ?? {};

        return deleteBlobsReviewResultExternalIdBlobId(
            externalId,
            blobId,
            axiosOptions
        );
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteBlobsReviewResultExternalIdBlobIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>
>;

export type DeleteBlobsReviewResultExternalIdBlobIdMutationError =
    AxiosError<unknown>;

export const useDeleteBlobsReviewResultExternalIdBlobId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>,
            TError,
            {
                externalId: ControlResultSuperSubsequentStepExternalId;
                blobId: BlobId;
            },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof deleteBlobsReviewResultExternalIdBlobId>>,
    TError,
    { externalId: ControlResultSuperSubsequentStepExternalId; blobId: BlobId },
    TContext
> => {
    return useMutation(
        getDeleteBlobsReviewResultExternalIdBlobIdMutationOptions(options),
        queryClient
    );
};
export const postBlobs = (
    postBlobsBody: Blob[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlobListDto>> => {
    const formData = new FormData();
    postBlobsBody.forEach((value) => formData.append("data", value));

    return axios.post(`/api/blobs`, formData, options);
};

export const getPostBlobsMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobs>>,
        TError,
        { data: Blob[] },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobs>>,
    TError,
    { data: Blob[] },
    TContext
> => {
    const mutationKey = ["postBlobs"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobs>>,
        { data: Blob[] }
    > = (props) => {
        const { data } = props ?? {};

        return postBlobs(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobs>>
>;
export type PostBlobsMutationBody = Blob[];
export type PostBlobsMutationError = AxiosError<unknown>;

export const usePostBlobs = <TError = AxiosError<unknown>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobs>>,
            TError,
            { data: Blob[] },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobs>>,
    TError,
    { data: Blob[] },
    TContext
> => {
    return useMutation(getPostBlobsMutationOptions(options), queryClient);
};
export const postBlobsDuplicate = (
    blobId: BlobId[],
    options?: AxiosRequestConfig
): Promise<AxiosResponse<SavedBlob[]>> => {
    return axios.post(`/api/blobs/duplicate`, blobId, options);
};

export const getPostBlobsDuplicateMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof postBlobsDuplicate>>,
        TError,
        { data: BlobId[] },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof postBlobsDuplicate>>,
    TError,
    { data: BlobId[] },
    TContext
> => {
    const mutationKey = ["postBlobsDuplicate"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof postBlobsDuplicate>>,
        { data: BlobId[] }
    > = (props) => {
        const { data } = props ?? {};

        return postBlobsDuplicate(data, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type PostBlobsDuplicateMutationResult = NonNullable<
    Awaited<ReturnType<typeof postBlobsDuplicate>>
>;
export type PostBlobsDuplicateMutationBody = BlobId[];
export type PostBlobsDuplicateMutationError = AxiosError<unknown>;

export const usePostBlobsDuplicate = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof postBlobsDuplicate>>,
            TError,
            { data: BlobId[] },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof postBlobsDuplicate>>,
    TError,
    { data: BlobId[] },
    TContext
> => {
    return useMutation(
        getPostBlobsDuplicateMutationOptions(options),
        queryClient
    );
};
export const getBlobsBlobId = (
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<Blob>> => {
    return axios.get(`/api/blobs/${blobId}`, {
        responseType: "blob",
        paramsSerializer: { indexes: null },
        ...options,
    });
};

export const getGetBlobsBlobIdQueryKey = (blobId: BlobId) => {
    return [`/api/blobs/${blobId}`] as const;
};

export const getGetBlobsBlobIdQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetBlobsBlobIdQueryKey(blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsBlobId>>
    > = ({ signal }) => getBlobsBlobId(blobId, { signal, ...axiosOptions });

    return {
        queryKey,
        queryFn,
        enabled: blobId !== null && blobId !== undefined,
        ...queryOptions,
    } as UseQueryOptions<
        Awaited<ReturnType<typeof getBlobsBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsBlobIdQueryResult = NonNullable<
    Awaited<ReturnType<typeof getBlobsBlobId>>
>;
export type GetBlobsBlobIdQueryError = AxiosError<unknown>;

export function useGetBlobsBlobId<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options: {
        query: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getBlobsBlobId>>,
                    TError,
                    Awaited<ReturnType<typeof getBlobsBlobId>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsBlobId<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<typeof getBlobsBlobId>>,
                    TError,
                    Awaited<ReturnType<typeof getBlobsBlobId>>
                >,
                "initialData"
            >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsBlobId<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsBlobId<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetBlobsBlobIdQueryOptions(blobId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<
        TData,
        TError
    > & { queryKey: DataTag<QueryKey, TData, TError> };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetBlobsBlobIdSuspenseQueryOptions = <
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    }
) => {
    const { query: queryOptions, axios: axiosOptions } = options ?? {};

    const queryKey =
        queryOptions?.queryKey ?? getGetBlobsBlobIdQueryKey(blobId);

    const queryFn: QueryFunction<
        Awaited<ReturnType<typeof getBlobsBlobId>>
    > = ({ signal }) => getBlobsBlobId(blobId, { signal, ...axiosOptions });

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof getBlobsBlobId>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBlobsBlobIdSuspenseQueryResult = NonNullable<
    Awaited<ReturnType<typeof getBlobsBlobId>>
>;
export type GetBlobsBlobIdSuspenseQueryError = AxiosError<unknown>;

export function useGetBlobsBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options: {
        query: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBlobsBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBlobsBlobIdSuspense<
    TData = Awaited<ReturnType<typeof getBlobsBlobId>>,
    TError = AxiosError<unknown>,
>(
    blobId: BlobId,
    options?: {
        query?: Partial<
            UseSuspenseQueryOptions<
                Awaited<ReturnType<typeof getBlobsBlobId>>,
                TError,
                TData
            >
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const queryOptions = getGetBlobsBlobIdSuspenseQueryOptions(blobId, options);

    const query = useSuspenseQuery(
        queryOptions,
        queryClient
    ) as UseSuspenseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const deleteBlobsBlobId = (
    blobId: BlobId,
    options?: AxiosRequestConfig
): Promise<AxiosResponse<void>> => {
    return axios.delete(`/api/blobs/${blobId}`, options);
};

export const getDeleteBlobsBlobIdMutationOptions = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<typeof deleteBlobsBlobId>>,
        TError,
        { blobId: BlobId },
        TContext
    >;
    axios?: AxiosRequestConfig;
}): UseMutationOptions<
    Awaited<ReturnType<typeof deleteBlobsBlobId>>,
    TError,
    { blobId: BlobId },
    TContext
> => {
    const mutationKey = ["deleteBlobsBlobId"];
    const { mutation: mutationOptions, axios: axiosOptions } = options
        ? options.mutation &&
          "mutationKey" in options.mutation &&
          options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, axios: undefined };

    const mutationFn: MutationFunction<
        Awaited<ReturnType<typeof deleteBlobsBlobId>>,
        { blobId: BlobId }
    > = (props) => {
        const { blobId } = props ?? {};

        return deleteBlobsBlobId(blobId, axiosOptions);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteBlobsBlobIdMutationResult = NonNullable<
    Awaited<ReturnType<typeof deleteBlobsBlobId>>
>;

export type DeleteBlobsBlobIdMutationError = AxiosError<unknown>;

export const useDeleteBlobsBlobId = <
    TError = AxiosError<unknown>,
    TContext = unknown,
>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<typeof deleteBlobsBlobId>>,
            TError,
            { blobId: BlobId },
            TContext
        >;
        axios?: AxiosRequestConfig;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<typeof deleteBlobsBlobId>>,
    TError,
    { blobId: BlobId },
    TContext
> => {
    return useMutation(
        getDeleteBlobsBlobIdMutationOptions(options),
        queryClient
    );
};
